import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import FallbackImg from "../../../components/FallbackImg";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import ErrorIcon from "../../../assets/aftersalesservice_icons/error_code_icon.svg";
import SearchImg from "../../../assets/aftersalesservice_icons/search.png";
import { useLanguage } from "../LanguageContext";
import ApiService from "../../../components/Api_services";
import PreviewLayout from "./PreviewLayout";

const SectionIcon = ({ name, color, size = 20 }) => {
  const IconComp = name && LucideIcons[name];
  return IconComp ? (
    <IconComp
      color={color || "#000000"}
      size={size}
      style={{ flexShrink: 0 }}
    />
  ) : null;
};

const PreviewErrorCode = () => {
  const { trans, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get("item_id");
  const [errorCode, setErrorCode] = useState(null);

  const data = useMemo(
    () =>
      (errorCode || []).map((item) => ({
        error_indicator: item.code,
        sections: (item.sections || [])
          .slice()
          .sort((a, b) => (Number(a.seq_no) || 0) - (Number(b.seq_no) || 0))
          .map((s) => ({
            label: s.description || "",
            icon: s.icon,
            text_color: s.text_color,
            background_color: s.background_color,
            children: (s.children || [])
              .map((c) => ({
                description: c.description || "",
                text_color: c.text_color,
              }))
              .filter((c) => c.description),
          })),
      })),
    [errorCode],
  );
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState(data);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setFiltered(data);
  }, [data]);

  useEffect(() => {
    const q = query.toLowerCase().trim();
    if (q === "") {
      setFiltered(data);
      return;
    }

    const filteredData = data.filter((error) => {
      const indicator = (error.error_indicator || "").toLowerCase();
      const sectionMatch = (error.sections || []).some((s) => {
        const label = (s.label || "").toLowerCase();
        const childMatch = (s.children || []).some((c) =>
          (c.description || "").toLowerCase().includes(q),
        );
        return label.includes(q) || childMatch;
      });

      return indicator.includes(q) || sectionMatch;
    });

    setFiltered(filteredData);
  }, [query, data]);

  useEffect(() => {
    const fetchErrorCode = async () => {
      const apiLang = lang === "ch" ? "cn" : lang === "my" ? "ms" : lang;

      try {
        const response = await ApiService.getErrorCode(itemId, apiLang);
        if (response && response.status === 200) {
          setErrorCode(response.data);
        } else {
          console.error("Error fetching error code:", response.message);
        }
      } catch (error) {
        console.error("Error fetching error code:", error);
      }
    };

    fetchErrorCode();
  }, [itemId, lang]);

  const showErrorDetails = (error) => {
    setSelected(error);
  };

  const closeModal = () => setSelected(null);

  return (
    <PreviewLayout>
      <div className="as-error-code-content" id="error_code_modal">
        <div style={{ padding: "0 31.97px" }}>
          <div className="as-error-code-container">
            <button
              type="button"
              className="as-back-btn"
              id="error_code_back"
              onClick={() =>
                navigate(`/aftersalesservice/preview?item_id=${itemId}`)
              }
            >
              <FallbackImg src={BackBlue} alt="back_blue_icon" />
              {trans.back}
            </button>

            <div className="as-error-code-main">
              <FallbackImg src={ErrorIcon} alt="error_code_icon" />
              <div>
                <div className="as-error-code-main-title">
                  {trans.error_code}
                </div>
                <div className="as-error-code-main-text">
                  {trans.error_code_sub}
                </div>
              </div>
            </div>

            <div className="as-error-code-searchbar">
              <FallbackImg src={SearchImg} alt="search" id="search" />
              <input
                placeholder="Search error code..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="as-error-code-count">
              {filtered.length}{" "}
              {filtered.length === 1
                ? trans.error_code_singular
                : trans.error_code_plural}
            </div>
          </div>

          <div className="as-error-code-container">
            <div
              className="as-ewarranty-troubleshoot-list"
              id="ewarranty-troubleshoot-list"
              style={{ display: filtered.length ? "grid" : "none" }}
            >
              {filtered.map((error, idx) => (
                <div
                  key={idx}
                  className={`as-ewarranty-troubleshoot-box${selected === error ? " as-active" : ""}`}
                  onClick={() => showErrorDetails(error)}
                >
                  {error.error_indicator}
                </div>
              ))}
            </div>

            <div
              className="as-no-results"
              id="no-results"
              style={{ display: filtered.length ? "none" : "block" }}
            >
              {trans.no_error}
            </div>
          </div>

          <div className="as-contact-support-container">
            <div className="as-contact-support-text">{trans.cant_find}</div>
            <div className="as-contact-support-text">{trans.contact_our}</div>

            <button
              type="button"
              className="as-contact-support-btn"
              id="error-contact-support-btn"
            >
              {trans.contact_sup}
            </button>
          </div>
        </div>

        <div id="modal-container">
          {selected && (
            <div className="as-troubleshoot-error-modal">
              <div className="as-troubleshoot-error-content">
                <div className="as-troubleshoot-error-header">
                  <div className="as-troubleshoot-error-code-container">
                    <div className="as-troubleshoot-error-code">
                      {selected.error_indicator}
                    </div>
                    <div className="as-troubleshoot-error-title">
                      {trans.error_code}
                      <br />
                      {trans.error_details}
                    </div>
                  </div>
                  <button
                    className="as-close-troubleshoot"
                    id="close-troubleshoot"
                    onClick={closeModal}
                  >
                    &times;
                  </button>
                </div>

                <div className="as-troubleshoot-error-main">
                  {selected.sections?.map((section, idx) => (
                    <div
                      key={idx}
                      className="as-troubleshoot-error-item"
                      style={{
                        backgroundColor: section.background_color
                          ? `${section.background_color}80`
                          : "#ffffff",
                        border: section.text_color
                          ? `1px solid ${section.text_color}`
                          : "1px solid #000000",
                      }}
                    >
                      <div
                        className="as-troubleshoot-error-icon"
                        style={{
                          backgroundColor: section.background_color
                            ? section.background_color
                            : "#f0f0f0",
                        }}
                      >
                        <SectionIcon
                          name={section.icon}
                          color={section.text_color}
                        />
                      </div>
                      <div className="as-troubleshoot-error-subitem">
                        <div
                          className="as-troubleshoot-error-title"
                          style={{
                            color: section.text_color
                              ? section.text_color
                              : "#000000",
                          }}
                        >
                          {section.label}
                        </div>
                        {section.children.map((c, i) => (
                          <div
                            className="as-troubleshoot-error-text"
                            key={i}
                            style={{
                              color: c.text_color ? c.text_color : "#000000",
                            }}
                          >
                            {c.description}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div>
                    <button
                      type="button"
                      className="as-back-troubleshoot"
                      id="back-troubleshoot"
                      onClick={closeModal}
                    >
                      {trans.back_error}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PreviewLayout>
  );
};

export default PreviewErrorCode;
